import { groupBy, mapValues } from 'lodash-es'
import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { RequestStatus } from './ParticipationRequest.ts'

interface Response {
    responsesAverageTime?: number
    responsesAmount?: number
}

import {
    durationProjectToLabelMap,
    durationToLabelMap,
    experienceToLabelMap,
    howIdToLabelMap,
    whoIdToLabelMap
} from '../../i18n/mentoring/sk/expertise.ts'

export type MentoringId = string

export enum MentoringCategory {
    Who = 'who',
    Topics = 'topics',
    Maturity = 'maturity',
    How = 'how',
    Duration = 'duration',
    DurationProject = 'durationProject',
    Experience = 'experience'
}

// Maybe it would be a good thing to do...
// ... - move this enum to more suitable (global) file than this
export enum StatusChangedBy {
    Mentee = 'mentee',
    Mentor = 'mentor',
    Talent = 'talent',
    Org = 'org',
    Volunteer = 'volunteer'
}

export type Mentoring = DocumentWithTimestamps &
    Response & {
        id?: MentoringId
        menteeId: string
        mentorId: string
        status: RequestStatus
        menteeMeta: {
            firstName: string
            lastName: string
            contactPhone?: string
            contactEmail?: string
            parentEmail?: string
        }
        mentorMeta: {
            firstName: string
            lastName: string
            contactPhone?: string
            contactEmail?: string
        }
        agreement: {
            mentorGoals?: string
            mentorResponsibilities?: string
            menteeGoals?: string
            menteeResponsibilities?: string
            how?: string[]
            unscheduledMeetings?: boolean
            confirmedByMentor?: boolean
            confirmedByMentee?: boolean
            duration?: string
        }
        statusChangedBy?: StatusChangedBy
        mentoringType?: string
        formOfMentoring?: string
        oneTimeMentoring?: boolean
        messageForMentor?: string
        messageForMentee?: string
        feedbackForMentor?: string
        feedbackForMentee?: string
        mentorEnded?: boolean
        acceptTerms: boolean
        acceptedAt?: Timestamp
        startedAt?: Timestamp
        startedBy?: string
        startConfirmedByMentor?: boolean
        startConfirmedByMentee?: boolean
        endedAt?: Timestamp
        endedBy?: string
        menteeGoal?: string
        mentorGoal?: string
        postponedUntil?: Timestamp
        postponedStartUntil?: Timestamp
        postponedMentorFeedbackUntil?: Timestamp
        postponedMenteeFeedbackUntil?: Timestamp
        deadlineAt?: Timestamp
        initiatedByMentor?: boolean
        archivedByIds?: string[]
        archivedAtById?: Record<string, Timestamp>
        expiredAt?: Timestamp
        expiredReason?: string
        externalConfirmation?: {
            source: 'linkedin' | 'email' | 'phone' | 'name'
            input: string
            matchedBy: string
            confidence: 'high' | 'medium'
            confirmedById: string
            confirmedAt: Timestamp
            note?: string
        }
    }

export interface AskForMentorFormData {
    mentoringNote: string
    oneTimeMentoring?: boolean
    shareEmail: boolean
    sharePhone: boolean
    acceptTerms: boolean
}

export interface AskForMenteeFormData {
    mentoringNote: string
    oneTimeMentoring?: boolean
    acceptTerms: boolean
}

export interface RejectMentoringFormData {
    messageForMentee: string
}

export interface FinishMentoringFormData {
    message?: string
    mentorEnded?: boolean
}
export type MenteeARequestLimits =
    | {
          ok: true
          val: Mentoring
      }
    | {
          ok: false
          err: 'ratelimit_day' | 'ratelimit_week' | 'ratelimit_unknown'
      }
    | {
          ok: false
          err: 'unknown'
          msg: string
      }

export const MentoringData = [
    {
        id: 'secondary_school_student',
        category: MentoringCategory.Who
    },
    {
        id: 'university_student',
        category: MentoringCategory.Who
    },
    {
        id: 'young_professional',
        category: MentoringCategory.Who
    },
    {
        id: 'professional',
        category: MentoringCategory.Who
    },
    {
        id: 'teacher',
        category: MentoringCategory.Who
    },
    {
        id: 'ngo_school',
        category: MentoringCategory.Who
    },
    {
        id: 'public_institution',
        category: MentoringCategory.Who
    },
    {
        id: 'organization_growing_company',
        category: MentoringCategory.Who
    },
    {
        id: 'secondary_school_project',
        category: MentoringCategory.Who
    },
    {
        id: 'remotely',
        category: MentoringCategory.How
    },
    {
        id: 'in_person',
        category: MentoringCategory.How
    },
    {
        id: 'once',
        category: MentoringCategory.Duration,
        info: 'Chcem rozdávať rady, odporúčania, zdieľať svoje lessons learned pre tých, ktorí nemajú záujem o dlhodobý rozvoj.'
    },
    {
        id: 'long_term',
        category: MentoringCategory.Duration,
        info: 'Chcem rozvíjať druhých v oblastiach, v ktorých som odborník/čka, viem poradiť, dávať spätnú väzbu a pomáhať druhým osobnostne aj profesijne rásť. Ideálne na pravidelnej báze.'
    },
    {
        id: 'once',
        category: MentoringCategory.DurationProject
    },
    {
        id: 'long_term',
        category: MentoringCategory.DurationProject
    },
    {
        id: 'yes',
        category: MentoringCategory.Experience
    },
    {
        id: 'no',
        category: MentoringCategory.Experience
    }
]

export interface MentoringParentConfirmationData {
    parentContactEmail: string
}

export const MentoringDataByCategory = groupBy(MentoringData, 'category')

export const MentoringCategoriesMapped = mapValues(MentoringDataByCategory, (categoryGroup: any[]) => {
    return categoryGroup.map((category: any) => {
        let hint = ''
        let label = `${category.category}_${category.id}`
        if (category.category === MentoringCategory.Who) {
            label = whoIdToLabelMap[category.id]?.label ?? `${category.category}_${category.id}`
            hint = whoIdToLabelMap[category.id]?.hint || ''

            return {
                ...category,
                label,
                hint
            }
        }
        if (category.category === MentoringCategory.How) {
            label = howIdToLabelMap[category.id]?.label ?? `${category.category}_${category.id}`
        }
        if (category.category === MentoringCategory.Duration) {
            label = durationToLabelMap[category.id]?.label ?? `${category.category}_${category.id}`
        }
        if (category.category === MentoringCategory.DurationProject) {
            label = durationProjectToLabelMap[category.id]?.label ?? `${category.category}_${category.id}`
        }
        if (category.category === MentoringCategory.Experience) {
            label = experienceToLabelMap[category.id]?.label ?? `${category.category}_${category.id}`
        }
        return {
            ...category,
            label
        }
    })
})

export type MentoringCategoryItem = typeof MentoringData

export const categoriesByCategoryAndId = (ids: string[], categoryKey: MentoringCategory) => {
    return ids.map((id) => {
        let label = id
        if (categoryKey === MentoringCategory.Who) {
            label = whoIdToLabelMap[id]?.label ?? id
        }
        if (categoryKey === MentoringCategory.How) {
            label = howIdToLabelMap[id]?.label ?? id
        }
        if (categoryKey === MentoringCategory.Duration) {
            label = durationToLabelMap[id]?.label ?? id
        }
        return label
    })
}
